import React from "react";

import { separateInputWrapperPropsAndInputProps } from "../../../../../organisms/Form/utils";

import { TextEditor, TextEditorProps } from "../../../../../molecules/TextEditor";
import { usePyForm } from "../../../context";
import { BaseFormFieldProps, FormField } from "../../FormComponents/FormField";

// Get all unique props from InputText that aren't in BaseFormFieldProps
type UniqueInputProps = Omit<
	TextEditorProps,
	keyof BaseFormFieldProps<string> | "value" | "initialValue" | "onChange" | "onBlur"
>;

export type FormTextEditorProps = Omit<BaseFormFieldProps<string>, "input"> & UniqueInputProps;

/**
 * A form component that integrates the TextEditor with PYForm for rich text editing in forms.
 * It provides:
 * - Value binding to form state
 * - Change events updating form state
 * - Blur events triggering validation
 * - Error display from form schema
 * - Disabled state respect
 * - InputWrapper integration with labels and validation
 * - Accessibility features
 */
export const FormTextEditor = (props: FormTextEditorProps) => {
	const form = usePyForm();
	const { inputWrapperProps, inputProps } = separateInputWrapperPropsAndInputProps(props, form.id);

	if (!form) {
		throw new Error("FormTextEditor must be used within a Form component");
	}

	return (
		<FormField<string>
			{...inputWrapperProps}
			input={(field) => {
				return (
					<TextEditor
						{...inputProps}
						initialValue={field.value}
						onChange={field.onChange}
						onBlur={field.onBlur}
						id={inputWrapperProps.id}
						data-testid={`${inputWrapperProps["data-testid"]}-input-text`}
						disabled={inputWrapperProps.disabled}
					/>
				);
			}}
		/>
	);
};

export default FormTextEditor;
